// ============================================================
// AbrPolicy — codec-blind rendition selection for LL-HLS
// ============================================================
//
// The hls.js ABR refuses to switch between levels whose CODECS attributes
// differ in the first four characters (abr-controller findBestLevel skips any
// candidate with a different `codecSet`). On ladders where the source rung is
// HEVC and every transcode is AVC that permanently pins auto mode to the AVC
// rungs, whatever the bandwidth. The same filter makes the reverse direction
// worse: from the HEVC rung no AVC candidate matches either, so findBestLevel
// returns -1 and the fallback drops straight to the lowest rung.
//
// This policy ignores codec families entirely and plans purely on bitrate,
// buffer health and incident history. A cross-codec transition costs a
// SourceBuffer changeType and a decoder re-init on top of the normal switch, so
// it is held to stronger evidence — throughput proven by a saturating transfer
// rather than by part-sized samples — and to a longer cooldown.
//
// It never relaxes the hls.js emergency behaviour: the abandon rules that abort
// an in-flight fragment still run untouched, and while hls.js has an emergency
// level forced, or the buffer is nearly empty, this policy will not select
// anything above the hls.js choice.

export interface AbrLadderLevel {
  index: number;
  /** Bitrate to plan against (bits/s) — an average, not a peak, when known. */
  bitrate: number;
  /** hls.js codecSet ("avc1,mp4a"); levels differing here need a changeType. */
  codecSet: string;
  height: number;
  /** False when the codec is undecodable or the level has error history. */
  eligible: boolean;
  /**
   * Extra headroom multiplier for moving up to this level, 1 when none. Used for
   * renditions MediaCapabilities flags as decodable but not smooth: excluding
   * them outright would forfeit the top rung on a prediction that is frequently
   * wrong (HEVC on Windows especially), so instead they are held to a wider
   * margin and demoted for real by the dropped-frame guard if the prediction
   * turns out to be right.
   */
  penalty?: number;
}

export interface AbrPolicyInput {
  nowMs: number;
  /** Ascending by bitrate, as in hls.levels. */
  levels: AbrLadderLevel[];
  /** Level the last fragment was loaded from, -1 when nothing has loaded yet. */
  currentLevel: number;
  /** What the hls.js ABR decided — the emergency floor. */
  baseLevel: number;
  estimateBps: number;
  /**
   * Throughput proven by a saturating transfer (a full segment or a probe), 0
   * when none is recent. Part-sized transfers at the live edge only use a
   * fraction of the pipe, so this is the only evidence strong enough to justify
   * a codec-family change.
   */
  saturatedBps: number;
  /**
   * Throughput over the last few seconds only. Down-switches are decided on
   * this, never on `estimateBps`: the long, burst-lifted window needed to prove
   * headroom for an up-switch takes tens of seconds to register a collapse, and
   * an LL buffer is 2-3 s deep. 0 means nothing has loaded recently.
   */
  recentBps: number;
  estimateReady: boolean;
  bufferSeconds: number;
  /** Change in buffer length per second of wall clock; negative while draining. */
  bufferTrendPerSecond: number;
  /** Adaptive LL latency target; the up-switch buffer gate scales with it. */
  latencyTargetSeconds: number;
  /** hls.js emergency forced level (abandon rules), -1 when none. */
  forcedLevel: number;
  /** False when the platform cannot do SourceBuffer.changeType. */
  crossCodecAllowed: boolean;
  /**
   * True while the engine has suspended latency control to rebuild buffer
   * headroom after a stall. Adding a heavier rendition — or a probe — during
   * that window works directly against the recovery it is waiting on.
   */
  recoveringBuffer: boolean;
}

export interface AbrDecision {
  level: number;
  reason: string;
}

export type AbrIncident =
  | 'stall'
  | 'fragment-error'
  | 'abandon'
  | 'dropped-frames';

export interface AbrPolicyOptions {
  /** Headroom required to move up within the same codec family. */
  upHeadroom?: number;
  /**
   * Require saturating evidence (a full segment or a probe) before any
   * up-switch. Part-sized transfers at the live edge measure burst capacity at a
   * low duty cycle, which on a fast link reads as hundreds of Mbit/s and
   * promotes straight to the top rung.
   */
  requireSaturatedForUpSwitch?: boolean;
  /** Quiet period after the drain rule acts, so it gives up one rung, not the ladder. */
  drainCooldownMs?: number;
  /** How long a rung abandoned for lack of bandwidth is kept out of reach. */
  abandonedRungBlockMs?: number;
  /** Below `bitrate * keepFactor` the current level is given up. */
  keepFactor?: number;
  /** Quiet period after any up-switch before another one is considered. */
  upCooldownMs?: number;
  /** Quiet period after a cross-codec up-switch. */
  crossCodecUpCooldownMs?: number;
  /** Quiet period after a stall, abandon or fragment error. */
  incidentCooldownMs?: number;
  /** Buffer floor for up-switching, as a fraction of the latency target. */
  upBufferFraction?: number;
  /** Absolute buffer floor for up-switching (seconds). */
  upBufferMinSeconds?: number;
  /** Below this the buffer is treated as an emergency and hls.js leads. */
  criticalBufferSeconds?: number;
  /** Buffer level under which a sustained drain forces a step down. */
  drainFloorSeconds?: number;
  /** Step down when the buffer is on course to empty within this many seconds. */
  drainHorizonSeconds?: number;
  /** Drain rate (seconds of buffer lost per second) that counts as losing ground. */
  drainRatePerSecond?: number;
  /** First block applied to a level that failed right after being selected. */
  probationBaseMs?: number;
  /** Cap on the exponential probation block. */
  probationMaxMs?: number;
  /** Extra headroom per recorded failure of a level. */
  probationHeadroomStep?: number;
  /** Incident-free time at a level before one recorded failure is forgiven. */
  probationDecayMs?: number;
  /** A level is worth probing when the estimate is at least this share of what it needs. */
  probeRatio?: number;
  /** Quiet period between bandwidth probes. */
  probeCooldownMs?: number;
}

const DEFAULTS: Required<AbrPolicyOptions> = {
  upHeadroom: 1.3,
  requireSaturatedForUpSwitch: true,
  keepFactor: 0.95,
  upCooldownMs: 6_000,
  crossCodecUpCooldownMs: 12_000,
  incidentCooldownMs: 8_000,
  upBufferFraction: 0.6,
  upBufferMinSeconds: 1.5,
  criticalBufferSeconds: 0.6,
  // The floor sits below the steady-state LL buffer (~2.5-3 s at the 2.7 s
  // target). The rate must clear measurement noise, not the latency controller:
  // playback-rate effects are already cancelled in bufferTrend.ts, but the
  // part-append sawtooth still shows up to -0.19 s/s on a healthy stream, while
  // a real collapse (3.2 Mbps against a 12.8 Mbps rendition) drains ~0.75.
  drainCooldownMs: 3_000,
  abandonedRungBlockMs: 20_000,
  drainFloorSeconds: 2.0,
  drainHorizonSeconds: 4.0,
  drainRatePerSecond: 0.35,
  probationBaseMs: 30_000,
  probationMaxMs: 300_000,
  probationHeadroomStep: 0.25,
  probationDecayMs: 60_000,
  probeRatio: 0.45,
  probeCooldownMs: 20_000,
};

interface Probation {
  failures: number;
  blockedUntilMs: number;
}

export class AbrPolicy {
  private readonly opts: Required<AbrPolicyOptions>;
  private readonly probation = new Map<number, Probation>();
  // "Never happened" must not read as "happened at time zero": performance.now()
  // starts near zero, so a 0 here held every cold start in incident-cooldown for
  // the whole cooldown before the first up-switch could even be considered.
  private lastUpSwitchAtMs = Number.NEGATIVE_INFINITY;
  private lastIncidentAtMs = Number.NEGATIVE_INFINITY;
  private lastProbeAtMs = Number.NEGATIVE_INFINITY;
  private lastDrainAtMs = Number.NEGATIVE_INFINITY;
  private activeLevel = -1;
  private activeSinceMs = 0;

  constructor(options: AbrPolicyOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  reset(): void {
    this.probation.clear();
    this.lastUpSwitchAtMs = Number.NEGATIVE_INFINITY;
    this.lastIncidentAtMs = Number.NEGATIVE_INFINITY;
    this.lastProbeAtMs = Number.NEGATIVE_INFINITY;
    this.lastDrainAtMs = Number.NEGATIVE_INFINITY;
    this.activeLevel = -1;
    this.activeSinceMs = 0;
  }

  /** Records that playback is now running on `level`, decaying old failures. */
  noteLevelActive(level: number, nowMs: number): void {
    if (this.activeLevel === level) {
      const record = this.probation.get(level);
      if (
        record &&
        record.failures > 0 &&
        nowMs - this.activeSinceMs >= this.opts.probationDecayMs
      ) {
        record.failures -= 1;
        record.blockedUntilMs = 0;
        this.activeSinceMs = nowMs;
        if (record.failures === 0) this.probation.delete(level);
      }
      return;
    }
    this.activeLevel = level;
    this.activeSinceMs = nowMs;
  }

  /**
   * Records a playback incident. When it happened on a level we had just moved
   * up to, that level goes on an exponentially growing probation so the policy
   * stops proposing a rendition the connection or the decoder cannot sustain.
   */
  noteIncident(kind: AbrIncident, level: number, nowMs: number): void {
    this.lastIncidentAtMs = nowMs;
    if (level < 0) return;
    // A stall long after the last up-switch says nothing about the level itself
    // (CDN hiccup, latency catch-up); only blame the level while it is on trial.
    if (
      kind === 'stall' &&
      nowMs - this.lastUpSwitchAtMs > this.opts.incidentCooldownMs
    ) {
      return;
    }
    const record = this.probation.get(level) ?? { failures: 0, blockedUntilMs: 0 };
    record.failures += 1;
    const block = Math.min(
      this.opts.probationBaseMs * Math.pow(2, record.failures - 1),
      this.opts.probationMaxMs,
    );
    record.blockedUntilMs = nowMs + block;
    this.probation.set(level, record);
  }

  /**
   * Keeps a rung we just gave up out of reach for a while. Without this the
   * up-switch cooldown alone lets the ladder bounce back to the level that had
   * just failed to be delivered.
   */
  private blockRung(level: number, nowMs: number): void {
    if (level < 0) return;
    const record = this.probation.get(level) ?? { failures: 0, blockedUntilMs: 0 };
    record.blockedUntilMs = Math.max(
      record.blockedUntilMs,
      nowMs + this.opts.abandonedRungBlockMs,
    );
    this.probation.set(level, record);
  }

  /** Diagnostics for logging. */
  probationOf(level: number): Probation | undefined {
    return this.probation.get(level);
  }

  private isBlocked(level: number, nowMs: number): boolean {
    const record = this.probation.get(level);
    return !!record && record.blockedUntilMs > nowMs;
  }

  private upHeadroomFor(candidate: AbrLadderLevel): number {
    const failures = this.probation.get(candidate.index)?.failures ?? 0;
    return (
      this.opts.upHeadroom *
      (candidate.penalty ?? 1) *
      (1 + this.opts.probationHeadroomStep * failures)
    );
  }

  /**
   * Throughput a candidate may be judged against. A codec-family change costs a
   * SourceBuffer changeType and a decoder re-init, so it is only made on
   * evidence from a saturating transfer rather than on part-sized samples.
   */
  private availableBpsFor(input: AbrPolicyInput): number {
    return this.opts.requireSaturatedForUpSwitch
      ? input.saturatedBps
      : input.estimateBps;
  }

  private upBufferFloor(input: AbrPolicyInput): number {
    return Math.max(
      this.opts.upBufferMinSeconds,
      this.opts.upBufferFraction * input.latencyTargetSeconds,
    );
  }

  private upCooldownFor(crossCodec: boolean): number {
    return crossCodec
      ? this.opts.crossCodecUpCooldownMs
      : this.opts.upCooldownMs;
  }

  decide(input: AbrPolicyInput): AbrDecision {
    const { levels, nowMs } = input;
    const eligible = levels.filter(l => l.eligible);
    if (eligible.length === 0) {
      return { level: input.baseLevel, reason: 'no-eligible-levels' };
    }
    if (!input.estimateReady) {
      return { level: input.baseLevel, reason: 'estimate-not-ready' };
    }

    const lowest = eligible[0].index;
    const current =
      input.currentLevel >= 0 ? input.currentLevel : input.baseLevel;
    const from = levels.find(l => l.index === current);
    const emergency =
      input.forcedLevel !== -1 ||
      input.bufferSeconds <= this.opts.criticalBufferSeconds;

    // --- Down / hold: highest eligible level at or below current we can still
    // afford on *recent* throughput. Using the long window here would keep a
    // rendition alive on evidence collected before the connection collapsed.
    const downBps =
      input.recentBps > 0
        ? Math.min(input.estimateBps, input.recentBps)
        : input.estimateBps;
    let keepLevel = lowest;
    for (let i = eligible.length - 1; i >= 0; i--) {
      const candidate = eligible[i];
      if (candidate.index > current) continue;
      if (downBps >= candidate.bitrate * this.opts.keepFactor) {
        keepLevel = candidate.index;
        break;
      }
    }
    if (keepLevel < current) {
      this.blockRung(current, nowMs);
      return {
        level: emergency ? Math.min(keepLevel, input.baseLevel) : keepLevel,
        reason: 'insufficient-bandwidth',
      };
    }

    // A shrinking buffer is direct evidence that the current rendition is not
    // being delivered fast enough, whatever any estimate says. Give up a rung
    // while there is still buffer left to give up.
    const draining =
      input.bufferTrendPerSecond <= -this.opts.drainRatePerSecond &&
      (input.bufferSeconds < this.opts.drainFloorSeconds ||
        input.bufferSeconds / -input.bufferTrendPerSecond <=
          this.opts.drainHorizonSeconds);
    if (draining) {
      // One rung at a time, then wait: the trend is only recomputed once per
      // second, and this decision is read many times per second. Without the
      // cooldown a single stale reading walks the whole ladder down in one tick.
      if (nowMs - this.lastDrainAtMs >= this.opts.drainCooldownMs) {
        const stepDown = eligible.filter(l => l.index < current).pop();
        if (stepDown) {
          this.lastDrainAtMs = nowMs;
          this.blockRung(current, nowMs);
          return { level: stepDown.index, reason: 'buffer-draining' };
        }
      }
      // Already on the lowest rung, or inside the cooldown: hold. Falling
      // through to the up-switch section while the buffer is visibly draining
      // is how a bottomed-out ladder bounced straight back to the top.
      return { level: current, reason: 'buffer-draining-hold' };
    }

    if (emergency) {
      return {
        level: Math.min(current, input.baseLevel),
        reason: input.forcedLevel !== -1 ? 'hls-forced' : 'buffer-critical',
      };
    }

    // --- Up: gated on buffer health, incident history and cooldowns
    if (input.recoveringBuffer) {
      return { level: current, reason: 'buffer-recovering' };
    }
    if (input.bufferSeconds < this.upBufferFloor(input)) {
      return { level: current, reason: 'buffer-below-up-floor' };
    }
    if (nowMs - this.lastIncidentAtMs < this.opts.incidentCooldownMs) {
      return { level: current, reason: 'incident-cooldown' };
    }

    for (let i = eligible.length - 1; i >= 0; i--) {
      const candidate = eligible[i];
      if (candidate.index <= current) break;
      if (this.isBlocked(candidate.index, nowMs)) continue;
      const crossCodec = !!from && from.codecSet !== candidate.codecSet;
      if (crossCodec && !input.crossCodecAllowed) continue;
      const need = candidate.bitrate * this.upHeadroomFor(candidate);
      if (this.availableBpsFor(input) < need) continue;
      // Sanity floor: never move up to a rendition the link is not delivering
      // right now, however good the long-window or probe evidence looks. A probe
      // stays valid for half a minute and must not outlive the conditions, and
      // no recent delivery at all is no evidence rather than good news.
      if (input.recentBps < candidate.bitrate) continue;
      if (nowMs - this.lastUpSwitchAtMs < this.upCooldownFor(crossCodec)) {
        return { level: current, reason: 'up-cooldown' };
      }
      this.lastUpSwitchAtMs = nowMs;
      return {
        level: candidate.index,
        reason: crossCodec ? 'up-switch-cross-codec' : 'up-switch',
      };
    }

    return { level: current, reason: 'hold' };
  }

  /**
   * The level worth spending a bandwidth probe on: the next rung up that the
   * current estimate cannot justify, but is close enough that a saturating
   * measurement could. Part-sized transfers at the live edge only ever use a
   * fraction of the pipe, so without a probe a fast connection can sit one rung
   * below its real capacity indefinitely.
   */
  probeTarget(input: AbrPolicyInput): number | null {
    const { levels, nowMs } = input;
    if (nowMs - this.lastProbeAtMs < this.opts.probeCooldownMs) return null;
    if (nowMs - this.lastIncidentAtMs < this.opts.incidentCooldownMs) return null;
    if (input.forcedLevel !== -1 || input.recoveringBuffer) return null;
    if (input.bufferSeconds < this.upBufferFloor(input)) return null;

    const current =
      input.currentLevel >= 0 ? input.currentLevel : input.baseLevel;
    const from = levels.find(l => l.index === current);
    const candidates = levels
      .filter(l => l.eligible && l.index > current)
      .sort((a, b) => a.index - b.index);

    for (const candidate of candidates) {
      if (this.isBlocked(candidate.index, nowMs)) continue;
      const crossCodec = !!from && from.codecSet !== candidate.codecSet;
      if (crossCodec && !input.crossCodecAllowed) continue;
      const need = candidate.bitrate * this.upHeadroomFor(candidate);
      if (this.availableBpsFor(input) >= need) {
        return null; // affordable on evidence we already have
      }
      if (input.estimateBps >= need * this.opts.probeRatio) {
        return candidate.index;
      }
      return null; // the next rung is far out of reach; higher ones are worse
    }
    return null;
  }

  noteProbe(nowMs: number): void {
    this.lastProbeAtMs = nowMs;
  }
}
